import { DragEvent, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Drawer,
    FormControlLabel,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    Typography,
    Divider,
} from "@mui/material";
import SettingsIcon from '@mui/icons-material/Settings';
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Translate from "component/global/translation/translation-component";
import { useTranslation } from "react-i18next";
import { ColumnPreference } from "../hooks/useColumnPreferences";
import { translateMetadataLabel } from "modules/record/shared/services/recordMetadataLabels";

interface RecordListColumnManagerProps {
    preferences: ColumnPreference[];
    disabled?: boolean;
    onSave: (prefs: ColumnPreference[]) => void;
}

const RecordListColumnManagerComponent = ({
    preferences,
    disabled = false,
    onSave,
}: RecordListColumnManagerProps) => {
    const [open, setOpen] = useState(false);
    const [local, setLocal] = useState<ColumnPreference[]>(preferences);
    const [draggingName, setDraggingName] = useState<string | null>(null);
    const { t } = useTranslation();
    const dragToReorderLabel = String(t("Drag to reorder"));
    const moveColumnUpLabel = String(t("Move column up"));
    const moveColumnDownLabel = String(t("Move column down"));

    const handleOpen = () => {
        setLocal([...preferences].sort((a, b) => a.order - b.order));
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = () => {
        onSave(local);
        setOpen(false);
    };

    const reorder = (fromIndex: number, toIndex: number) => {
        if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

        setLocal(prev => {
            const next = [...prev];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return next.map((p, i) => ({ ...p, order: i }));
        });
    };

    const toggleVisibility = (name: string) => {
        setLocal(prev =>
            prev.map(p => (p.name === name ? { ...p, visible: !p.visible } : p))
        );
    };

    const moveUp = (index: number) => {
        reorder(index, index - 1);
    };

    const moveDown = (index: number) => {
        reorder(index, index + 1);
    };

    const handleDragStart = (name: string) => (event: DragEvent) => {
        setDraggingName(name);
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", name);
    };

    const handleDragOver = (event: DragEvent) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    };

    const handleDrop = (targetName: string) => (event: DragEvent) => {
        event.preventDefault();
        const sourceName = event.dataTransfer.getData("text/plain") || draggingName;
        if (!sourceName || sourceName === targetName) return;

        const fromIndex = local.findIndex(pref => pref.name === sourceName);
        const toIndex = local.findIndex(pref => pref.name === targetName);
        reorder(fromIndex, toIndex);
        setDraggingName(null);
    };

    return (
        <>
            <Tooltip title={t("Manage columns")}>
                <span>
                    <IconButton
                        onClick={handleOpen}
                        aria-label={String(t("Manage columns"))}
                        size="small"
                        disabled={disabled || preferences.length === 0}
                        sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.92)",
                            color: "#3f3a3c",
                            "&:hover": {
                                backgroundColor: "#ffffff",
                            },
                            "&.Mui-disabled": {
                                backgroundColor: "rgba(255, 255, 255, 0.35)",
                            },
                        }}
                    >
                        <SettingsIcon />
                    </IconButton>
                </span>
            </Tooltip>

            <Drawer anchor="right" open={open} onClose={handleClose}>
                <Box sx={{ width: { xs: "100vw", sm: 380 }, display: "flex", flexDirection: "column", height: "100%" }}>
                    <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            <Translate text="Manage Columns" />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            <Translate text="Toggle visibility and reorder columns." />
                        </Typography>
                    </Box>

                    <Divider />

                    <Box sx={{ flex: 1, overflowY: "auto", px: { xs: 1.25, sm: 1.5 }, py: 1 }}>
                        <List disablePadding>
                            {local.map((pref, index) => (
                                <ListItem
                                    key={pref.name}
                                    disableGutters
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop(pref.name)}
                                    onDragEnd={() => setDraggingName(null)}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        minHeight: { xs: 52, sm: 44 },
                                        px: { xs: 1, sm: 1.25 },
                                        py: 0.5,
                                        borderRadius: 1,
                                        border: "1px solid transparent",
                                        opacity: draggingName === pref.name ? 0.45 : 1,
                                        transition: "background-color 0.15s, border-color 0.15s, opacity 0.15s",
                                        "&:hover": { backgroundColor: "action.hover" },
                                        ...(draggingName && draggingName !== pref.name
                                            ? { borderColor: "divider" }
                                            : {}),
                                    }}
                                >
                                    <Tooltip title={dragToReorderLabel}>
                                        <Box
                                            draggable
                                            onDragStart={handleDragStart(pref.name)}
                                            role="button"
                                            aria-label={dragToReorderLabel}
                                            sx={{
                                                display: { xs: "none", sm: "inline-flex" },
                                                alignItems: "center",
                                                color: "text.secondary",
                                                cursor: "grab",
                                                "&:active": { cursor: "grabbing" },
                                            }}
                                        >
                                            <DragIndicatorIcon fontSize="small" />
                                        </Box>
                                    </Tooltip>
                                    <FormControlLabel
                                        sx={{ flex: 1, mr: 0, minWidth: 0 }}
                                        control={
                                            <Checkbox
                                                checked={pref.visible}
                                                onChange={() => toggleVisibility(pref.name)}
                                                size="small"
                                            />
                                        }
                                        label={
                                            <ListItemText
                                                primary={translateMetadataLabel(t, pref)}
                                                primaryTypographyProps={{ variant: "body2" }}
                                            />
                                        }
                                    />

                                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                                        <IconButton
                                            size="small"
                                            title={moveColumnUpLabel}
                                            onClick={() => moveUp(index)}
                                            disabled={index === 0}
                                        >
                                            <ArrowUpwardIcon fontSize="inherit" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            title={moveColumnDownLabel}
                                            onClick={() => moveDown(index)}
                                            disabled={index === local.length - 1}
                                        >
                                            <ArrowDownwardIcon fontSize="inherit" />
                                        </IconButton>
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    </Box>

                    <Divider />

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                            p: { xs: 1.5, sm: 2 },
                            flexDirection: { xs: "column-reverse", sm: "row" },
                        }}
                    >
                        <Button variant="outlined" color="inherit" onClick={handleClose}>
                            <Translate text="Cancel" />
                        </Button>
                        <Button variant="contained" onClick={handleSave}>
                            <Translate text="Save" />
                        </Button>
                    </Box>
                </Box>
            </Drawer>
        </>
    );
};

export default RecordListColumnManagerComponent;
